import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useDepartments, usePositions } from '@/features/access/api'
import { api } from '@/lib/api/client'
import { asList, asRows } from '@/lib/api/list'

export type GeoProvince = { id: string; name: string }
export type GeoDistrict = { id: string; province_id: string; name: string }
export type HrDepartmentRef = { id: string; name: string }
export type LeaveTypeRef = { id: string; name: string; deductsFromAnnual: boolean; requiresDocument: boolean }

const DAY = 24 * 60 * 60_000

/** İl listesi statik referans veridir: oturum boyunca bir kez çekilir. */
export function useProvinces() {
  return useQuery({
    queryKey: ['geo', 'provinces'],
    queryFn: async () => asList(await api.get<GeoProvince[]>('/api/v1/geo/provinces')),
    staleTime: Infinity,
    gcTime: DAY,
  })
}

export function useDistricts(provinceId: string) {
  return useQuery({
    queryKey: ['geo', 'districts', provinceId],
    queryFn: async () => asList(await api.get<GeoDistrict[]>('/api/v1/geo/districts', { province_id: provinceId })),
    enabled: !!provinceId,
    staleTime: Infinity,
    gcTime: DAY,
  })
}

/** Departmanlar Erişim modülüyle aynı önbellekten (`list-all`) gelir. */
export function useHrDepartments() {
  const q = useDepartments()
  const data = useMemo<HrDepartmentRef[]>(() => (q.data ?? []).filter((d) => d.isActive).map((d) => ({ id: d.id, name: d.name })), [q.data])
  return { ...q, data }
}

/** Seçili departmanın aktif pozisyonları (pozisyon önbelleği paylaşılır, filtre istemcide). */
export function useDepartmentPositions(departmentId: string) {
  const q = usePositions()
  const data = useMemo(
    () => (q.data ?? []).filter((p) => p.isActive && (!departmentId || p.departmentId === departmentId)).map((p) => ({ id: p.id, name: p.title })),
    [q.data, departmentId],
  )
  return { ...q, data }
}

/** Daha önce girilmiş kurum adları (serbest metin alanı için öneri listesi). */
export function useUniversities() {
  return useQuery({
    queryKey: ['hr', 'universities'],
    queryFn: async () => asList(await api.get<string[]>('/api/v1/hr/universities')),
    staleTime: 5 * 60_000,
  })
}

export function useLeaveTypes() {
  return useQuery({
    queryKey: ['hr', 'leave-types'],
    staleTime: Infinity,
    queryFn: async (): Promise<LeaveTypeRef[]> =>
      asRows(await api.get<unknown>('/api/v1/hr/leaves/types')).map((row) => ({
        id: String(row.id ?? ''),
        name: String(row.name ?? ''),
        deductsFromAnnual: row.deducts_from_annual === true,
        requiresDocument: row.requires_document === true,
      })),
  })
}
